<template>
  <div class="RendererHtml">
    <pre v-if="viewMode === 'code'" ref="codeEl" class="RendererHtml__code">
      {{ content }}
    </pre>
    <iframe
      v-else
      :srcdoc="sanitizedSrcdoc"
      :sandbox="GENERATED_HTML_SANDBOX"
      :class="[
        'RendererHtml__frame',
        { 'RendererHtml__frame--resizing': isResizing },
      ]"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'

import { GENERATED_HTML_SANDBOX } from '@/constants/generatedHtmlSandbox'
import type { HtmlPreviewViewMode } from '@/store/preview'
import { previewScrollbarStyleSource } from '@/utils/previewScrollbarStyle'
import { publishedHtmlLinkScriptSource } from '@/utils/publishedHtmlLinks'
import { sandboxedStorageShimScriptSource } from '@/utils/sandboxedStorageShim'

const props = defineProps<{
  content: string
  isResizing: boolean
  viewMode?: HtmlPreviewViewMode
}>()

const codeEl = ref<HTMLElement | null>(null)

watch(
  () => props.content,
  async () => {
    if (props.viewMode !== 'code') return
    await nextTick()
    const el = codeEl.value
    if (!el) return
    el.scrollTop = el.scrollHeight
  },
)

function ensureHead(doc: Document): HTMLHeadElement {
  let head = doc.querySelector('head')
  if (head) return head
  head = doc.createElement('head')
  const htmlEl = doc.documentElement
  const insertBefore = htmlEl.querySelector('body')
  if (insertBefore) {
    htmlEl.insertBefore(head, insertBefore)
  } else {
    htmlEl.insertBefore(head, htmlEl.firstChild)
  }
  return head
}

const sanitizedSrcdoc = computed(() => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(props.content, 'text/html')

  const cspMetaTags = doc.querySelectorAll(
    'meta[http-equiv="Content-Security-Policy" i]',
  )

  cspMetaTags.forEach((meta) => meta.remove())

  const head = ensureHead(doc)

  // Ahead of the page's own styles, so a page that styles its scrollbar still wins.
  const scrollbarStyle = doc.createElement('style')
  scrollbarStyle.textContent = previewScrollbarStyleSource()
  head.insertBefore(scrollbarStyle, head.firstChild)

  const linkScript = doc.createElement('script')
  linkScript.textContent = publishedHtmlLinkScriptSource()
  head.insertBefore(linkScript, head.firstChild)

  // Prepended last so it lands first: the shim must exist before any page script runs.
  const storageShimScript = doc.createElement('script')
  storageShimScript.textContent = sandboxedStorageShimScriptSource()
  head.insertBefore(storageShimScript, head.firstChild)

  return doc.documentElement.outerHTML
})
</script>

<style lang="scss" scoped>
.RendererHtml {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.RendererHtml__frame {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: none;
  background: #fff;

  &.RendererHtml__frame--resizing {
    pointer-events: none;
  }
}

.RendererHtml__code {
  flex: 1;
  width: 100%;
  min-height: 0;
  margin: 0;
  @include customScrollbarInset(1rem);
  border: none;
  background: var(--bg-tertiary, #fafafa);
  color: $color-neutral-170;
  @include font-code-small-regular;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
